import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CSSProperties } from 'react';
import '../../index.scss';
import drag from '../../../../../../../assets/drag.png';
import arrow from '../../../../../../../assets/arrow.svg';
import InfoIcon from '../../../../../../../components/Icons/infoIcon';
import { FormItem } from '../../../../../../../api/types/expenseareas';
import { ClipIcon } from '../../../../../../../components/Icons/ClipIcon';

/** Только вёрстка поля — без useSortable (для DragOverlay). */
const FormItemCanvasInner = ({
    item,
    isRequired,
    withDragHandleIcon
}: {
    item: FormItem;
    isRequired: boolean;
    withDragHandleIcon?: boolean;
}) => {
    return (
        <>
            {(item?.type === 'short-answer' || item?.type === 'paragraph') && (
                <>
                    {withDragHandleIcon && <img src={drag} alt="" className="drag-overlay__icon" />}
                    <div>
                        <div className="draggable__row">
                            <div className="short-answer">
                                {item?.label ? item?.label : 'Label name'}
                                {isRequired ? <div className="required">*</div> : ''}
                            </div>{' '}
                            {item.link_id && (
                                <div className="draggable__linked">
                                    <ClipIcon /> Linked
                                </div>
                            )}
                        </div>
                        {item?.description && (
                            <div className="helper-text" style={{ whiteSpace: 'pre-wrap' }}>
                                {item?.description}
                            </div>
                        )}
                    </div>
                </>
            )}

            {item?.type === 'single-select' && (
                <>
                    {withDragHandleIcon && <img src={drag} alt="" className="drag-overlay__icon" />}
                    <div className="draggable__row">
                        <div className="single-select">
                            <div className="row">
                                {item?.label ? item?.label : 'Label name'}
                                {isRequired ? <div className="required">*</div> : ''}
                            </div>
                        </div>
                        {item.link_id && (
                            <div className="draggable__linked">
                                <ClipIcon /> Linked
                            </div>
                        )}
                    </div>
                    {item?.description && <div className="helper-text">{item?.description}</div>}

                    <div className="single-select__wrapper">
                        {item?.options?.map((elem, index) => {
                            return (
                                <div className="single-select__option" key={index}>
                                    <div className="single-select__radio"></div>{' '}
                                    {elem?.value ? elem.value : `Option ${index + 1}`}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
            {item?.type === 'multiple-choice' && (
                <>
                    {withDragHandleIcon && <img src={drag} alt="" className="drag-overlay__icon" />}
                    <div className="draggable__row">
                        <div className="single-select">
                            <div className="row">
                                {item?.label ? item?.label : 'Label name'}
                                {isRequired ? <div className="required">*</div> : ''}
                            </div>
                        </div>{' '}
                        {item.link_id && (
                            <div className="draggable__linked">
                                <ClipIcon /> Linked
                            </div>
                        )}
                    </div>
                    {item?.description && <div className="helper-text">{item?.description}</div>}
                    <div className="single-select__wrapper">
                        {item?.options?.map((elem, index) => {
                            return (
                                <div className="single-select__option" style={{ whiteSpace: 'pre-wrap' }} key={index}>
                                    <div className="single-select__checkbox"></div>{' '}
                                    {elem?.value ? elem.value : `Option ${index + 1}`}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
            {item?.type === 'dropdown' && (
                <>
                    {withDragHandleIcon && <img src={drag} alt="" className="drag-overlay__icon" />}
                    <div className="draggable__row">
                        <div className="dropdown">
                            <div className="row">
                                {item?.label ? item?.label : 'Label name'}{' '}
                                {isRequired ? <div className="required">*</div> : ''}
                            </div>
                            <img src={arrow} alt="" />
                        </div>
                        {item.link_id && (
                            <div className="draggable__linked">
                                <ClipIcon /> Linked
                            </div>
                        )}
                    </div>
                    {item?.description && <div className="helper-text">{item?.description}</div>}
                </>
            )}
            {item?.type === 'file-upload' && (
                <>
                    {withDragHandleIcon && <img src={drag} alt="" className="drag-overlay__icon" />}
                    <div className="file-upload">
                        <div className="row">
                            {item?.label ? item?.label : 'Upload a file'}
                            {isRequired ? <div className="required">*</div> : ''}
                        </div>

                        {item?.description && <div className="helper-text">{item?.description}</div>}
                        <div className="file-upload__dropzone">
                            <h5>
                                Drop your file here or <span>Browse</span>
                            </h5>
                            <div>
                                {item?.settings
                                    ?.find((el) => el.type === 'file_upload_formats')
                                    ?.options?.filter((elem) => elem.selected)
                                    ?.map((elem) => elem.label)
                                    ?.join(', ')}{' '}
                                (max. 100 MB)
                            </div>
                        </div>
                    </div>
                </>
            )}
            {item?.type === 'explanation-banner' && (
                <>
                    {withDragHandleIcon && <img src={drag} alt="" className="drag-overlay__icon" />}
                    <div className="explanation-banner-label">
                        {item.link_id && (
                            <div className="draggable__linked" style={{ margin: '0 0 10px 0' }}>
                                <ClipIcon /> Linked
                            </div>
                        )}
                    </div>
                    <div className="explanation-banner" style={{ whiteSpace: 'pre-wrap' }}>
                        <InfoIcon fill="#001965" />
                        {item?.description ? item?.description : 'Please, enter explanation'}
                    </div>
                </>
            )}
        </>
    );
};

export const SortableFormItemDragOverlay = ({ item }: { item: FormItem }) => {
    const isRequired = item?.settings?.find((elem) => elem.type === 'required')?.switchValue;

    return (
        <div className="draggable drag-overlay" style={{ cursor: 'grabbing' }}>
            <FormItemCanvasInner item={item} isRequired={!!isRequired} withDragHandleIcon />
        </div>
    );
};

export const SortableFormItem = ({
    id,
    item,
    selectedElement,
    fieldsError,
    onSelect
}: {
    id: string;
    item: FormItem;
    selectedElement: null | string;
    fieldsError: string[];
    onSelect?: (id: string) => void;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : undefined,
        touchAction: 'none'
    };

    const isRequired = item?.settings?.find((elem) => elem.type === 'required')?.switchValue;

    const fieldHasClientError = fieldsError.some((fid) => String(fid) === String(id));
    const hasServerErr = Boolean(item.serverErrorMessage);
    const showFieldOutline = fieldHasClientError || hasServerErr;

    return (
        <>
            <div
                ref={setNodeRef}
                {...attributes}
                {...listeners}
                role="presentation"
                onClick={() => onSelect?.(String(item.id))}
                style={style}
                data-builder-field-id={String(id)}
                className={`draggable ${selectedElement === item.id + '' ? 'selected' : ''} ${
                    showFieldOutline ? 'error' : ''
                }`}
                key={item.id}
            >
                <FormItemCanvasInner item={item} isRequired={!!isRequired} />
            </div>
            {showFieldOutline && !isDragging && (
                <div className="error-text">
                    {item.serverErrorMessage || 'This field is not set yet. Please set field'}
                </div>
            )}
        </>
    );
};
